import { Company_User_Status } from "@generated/graphql";
import { Close } from "@mui/icons-material";
import { Button, IconButton, Stack } from "@mui/material";
import SearchIcon from "assets/icons/SearchIcon";
import TextField from "components/inputs/TextField";
import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { palette } from "theme";
import { TeamMembersFilter } from "./tableConfig/types";

type TeamFiltersProps = {
  onFilterChange: (filter: TeamMembersFilter) => void;
};

type FilterOption = {
  label: string;
  statuses?: Company_User_Status[];
};

const filterOptions: FilterOption[] = [
  { label: "Все" },
  { label: "Активные", statuses: [Company_User_Status.Active] },
  { label: "Ожидают", statuses: [Company_User_Status.Pending] },
  { label: "Деактивированные", statuses: [Company_User_Status.Deactivated] },
];

const getFilter = (search: string, statuses?: Company_User_Status[]): TeamMembersFilter => ({
  ...(search.trim() && { search: search.trim() }),
  ...(statuses && { statuses }),
});

const TeamFilters = ({ onFilterChange }: TeamFiltersProps) => {
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState(filterOptions[0]);
  const debouncedSearchChange = useMemo(
    () =>
      debounce((value: string, statuses?: Company_User_Status[]) => onFilterChange(getFilter(value, statuses)), 500),
    [onFilterChange]
  );

  useEffect(() => () => debouncedSearchChange.cancel(), [debouncedSearchChange]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    debouncedSearchChange(value, activeFilter.statuses);
  };

  const handleFilterChange = (filter: FilterOption) => {
    debouncedSearchChange.cancel();
    setActiveFilter(filter);
    onFilterChange(getFilter(search, filter.statuses));
  };

  const handleClearSearch = () => {
    debouncedSearchChange.cancel();
    setSearch("");
    onFilterChange(getFilter("", activeFilter.statuses));
  };

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      spacing={2}
      justifyContent='space-between'>
      <TextField
        value={search}
        onChange={handleSearchChange}
        placeholder='Поиск по имени или email'
        startIcon={<SearchIcon />}
        endIcon={
          search ? (
            <IconButton
              size='small'
              onClick={handleClearSearch}>
              <Close fontSize='small' />
            </IconButton>
          ) : undefined
        }
        variant='outlined'
        stackSx={{ width: { xs: "100%", md: 320 } }}
        sx={{
          "& .MuiOutlinedInput-root": {
            backgroundColor: "white",
            borderRadius: 3,
          },
        }}
      />
      <Stack
        direction='row'
        spacing={0.5}
        useFlexGap
        flexWrap='wrap'>
        {filterOptions.map((filter) => {
          const isActive = activeFilter.label === filter.label;

          return (
            <Button
              key={filter.label}
              variant={isActive ? "circleContained" : "circleOutlined"}
              onClick={() => handleFilterChange(filter)}
              sx={
                !isActive
                  ? { backgroundColor: "white !important", color: "text.secondary", borderColor: palette.border }
                  : {
                      backgroundColor: "transparent",
                      color: "text.secondary",
                      border: `1px solid ${palette.primary.main}`,
                      ":hover": {
                        backgroundColor: "transparent",
                        boxShadow: "0 2px 8px rgba(41, 82, 227, .6)",
                      },
                    }
              }>
              {filter.label}
            </Button>
          );
        })}
      </Stack>
    </Stack>
  );
};

export default TeamFilters;
